/**
 * Lector de extractos bancarios Norma 43 (AEB/CSB 43), clásico y SEPA.
 * Conserva TODOS los datos de cada movimiento (conceptos 23xx completos y
 * referencias), sin obligar a elegir entre "concepto 1" o "concepto 2".
 */

export interface N43Movement {
  index: number;
  operationDate: string; // YYYY-MM-DD
  valueDate: string;
  commonConcept: string; // código común AEB (2 dígitos)
  ownConcept: string; // código propio del banco (3 dígitos)
  /** Importe con signo: negativo = cargo, positivo = abono. */
  amount: number;
  documentNumber: string;
  reference1: string;
  reference2: string;
  /** Líneas de concepto complementarias (registros 23), en orden. */
  concepts: string[];
  /** Texto continuo de todos los registros 23, recomponiendo palabras partidas. */
  conceptText?: string;
}

export interface N43Account {
  sourceFormat?: "santander-txt";
  bank: string;
  office: string;
  account: string;
  startDate: string;
  endDate: string;
  initialBalance: number;
  currency: string;
  holder: string;
  movements: N43Movement[];
  /** Totales declarados en el registro 33. */
  declared: { debitCount: number; debitTotal: number; creditCount: number; creditTotal: number; finalBalance: number } | null;
  /** Totales calculados a partir de los movimientos. */
  computed: { debitCount: number; debitTotal: number; creditCount: number; creditTotal: number; finalBalance: number };
  balanced: boolean;
}

export const COMMON_CONCEPTS: Record<string, string> = {
  "01": "Talones / reintegros",
  "02": "Abonarés / entregas / ingresos",
  "03": "Domiciliados / recibos / letras",
  "04": "Giros / transferencias / traspasos",
  "05": "Amortizaciones de préstamos",
  "06": "Remesas de efectos",
  "07": "Suscripciones / dividendos",
  "08": "Divisas / extranjero",
  "09": "Valores",
  "10": "Cheques de gasolina",
  "11": "Cajeros automáticos",
  "12": "Tarjetas / TPV",
  "13": "Operaciones extranjero",
  "14": "Devoluciones e impagados",
  "15": "Nóminas / seguros sociales",
  "16": "Timbres",
  "17": "Intereses / comisiones / gastos",
  "98": "Anulaciones / correcciones",
  "99": "Varios",
};

const yymmdd = (s: string): string => {
  if (!/^\d{6}$/.test(s)) return "";
  const yy = Number(s.slice(0, 2));
  return `${yy < 70 ? 2000 + yy : 1900 + yy}-${s.slice(2, 4)}-${s.slice(4, 6)}`;
};
const cents = (s: string): number => {
  const n = Number((s || "").trim());
  return Number.isFinite(n) ? n / 100 : 0;
};
const r2 = (n: number) => Math.round(n * 100) / 100;

export function parseNorma43(text: string): N43Account[] {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim().length > 0);
  const accounts: N43Account[] = [];
  let current: N43Account | null = null;
  let last: N43Movement | null = null;

  for (const raw of lines) {
    const l = raw.padEnd(80, " ");
    const code = l.slice(0, 2);
    if (code === "11") {
      const sign = l[32] === "1" ? -1 : 1;
      current = {
        bank: l.slice(2, 6), office: l.slice(6, 10), account: l.slice(10, 20),
        startDate: yymmdd(l.slice(20, 26)), endDate: yymmdd(l.slice(26, 32)),
        initialBalance: sign * cents(l.slice(33, 47)), currency: l.slice(47, 50),
        holder: l.slice(51, 77).trim(), movements: [], declared: null,
        computed: { debitCount: 0, debitTotal: 0, creditCount: 0, creditTotal: 0, finalBalance: 0 },
        balanced: false,
      };
      accounts.push(current);
      last = null;
    } else if (code === "22" && current) {
      const sign = l[27] === "1" ? -1 : 1;
      last = {
        index: current.movements.length + 1,
        operationDate: yymmdd(l.slice(10, 16)), valueDate: yymmdd(l.slice(16, 22)),
        commonConcept: l.slice(22, 24), ownConcept: l.slice(24, 27),
        amount: sign * cents(l.slice(28, 42)),
        documentNumber: l.slice(42, 52).trim(), reference1: l.slice(52, 64).trim(), reference2: l.slice(64, 80).trim(),
        concepts: [],
      };
      current.movements.push(last);
    } else if (code === "23" && last) {
      const a = l.slice(4, 42).trim();
      const b = l.slice(42, 80).trim();
      if (a) last.concepts.push(a);
      if (b) last.concepts.push(b);
      // Las dos mitades se unen sin espacio: si el banco partió una palabra, se recompone.
      const joined = (l.slice(4, 42) + l.slice(42, 80)).replace(/\s+/g, " ").trim();
      if (joined) last.conceptText = last.conceptText ? `${last.conceptText} ${joined}` : joined;
    } else if (code === "33" && current) {
      const sign = l[58] === "1" ? -1 : 1;
      current.declared = {
        debitCount: Number(l.slice(20, 25)) || 0, debitTotal: cents(l.slice(25, 39)),
        creditCount: Number(l.slice(39, 44)) || 0, creditTotal: cents(l.slice(44, 58)),
        finalBalance: sign * cents(l.slice(59, 73)),
      };
    }
  }

  for (const acc of accounts) {
    const debits = acc.movements.filter((m) => m.amount < 0);
    const credits = acc.movements.filter((m) => m.amount >= 0);
    const debitTotal = r2(-debits.reduce((s, m) => s + m.amount, 0));
    const creditTotal = r2(credits.reduce((s, m) => s + m.amount, 0));
    acc.computed = {
      debitCount: debits.length, debitTotal, creditCount: credits.length, creditTotal,
      finalBalance: r2(acc.initialBalance - debitTotal + creditTotal),
    };
    const d = acc.declared;
    acc.balanced = !!d && d.debitCount === debits.length && d.creditCount === credits.length &&
      Math.abs(d.debitTotal - debitTotal) < 0.005 && Math.abs(d.creditTotal - creditTotal) < 0.005 &&
      Math.abs(d.finalBalance - acc.computed.finalBalance) < 0.005;
  }
  return accounts;
}

/** Texto completo del movimiento: conceptos continuos + referencias que no estén ya incluidas. */
export const fullConcept = (m: N43Movement) => {
  const text = m.conceptText ?? m.concepts.join(" ");
  const refs = [m.documentNumber, m.reference1, m.reference2]
    .filter((r) => r && !/^0+$/.test(r) && !text.includes(r));
  return [text, ...refs].filter(Boolean).join(" · ");
};
